import { useEffect, useState, useMemo } from "react";
import { api } from "../../services/api";
import toast from "react-hot-toast";
import { FaEye } from "react-icons/fa6";

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth <= 767);
  useEffect(() => {
    const h = () => setV(window.innerWidth <= 767);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return v;
}

/* ── Style injection ──────────────────────────────────────────── */
const STYLE_ID = "ceed-pp-styles";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .pp-wrap { font-family: 'DM Sans', sans-serif; color: #0c1118; }

    /* Page header */
    .pp-page-title {
      font-family: 'DM Sans', sans-serif;
      font-size: 24px; font-weight: 700; color: #0c1118; line-height: 1.2;
    }
    .pp-page-sub {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px; color: #6b7280; margin-top: 4px;
    }

    /* Btn variants */
    .pp-btn {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 8px 16px; border-radius: 7px; font-size: 13px; font-weight: 500;
      cursor: pointer; border: none; transition: background 150ms, box-shadow 150ms;
    }
    .pp-btn-dark { background: #0c1118; color: #fff; }
    .pp-btn-dark:hover { background: #1e2832; }
    .pp-btn-outline { background: #fff; color: #374151; border: 1px solid #e2e8f0; }
    .pp-btn-outline:hover { background: #f9fafb; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
    .pp-btn:disabled { opacity: .5; cursor: default; }

    /* Tabs */
    .pp-tabs { display: flex; gap: 0; border-bottom: 1px solid #e8ecf0; margin-bottom: 24px; }
    .pp-tab {
      padding: 10px 18px; font-size: 13.5px; font-weight: 500;
      cursor: pointer; background: none; border: none;
      border-bottom: 2px solid transparent; color: #9ca3af;
      transition: color 150ms, border-color 150ms;
    }
    .pp-tab.active { color: #0c1118; border-bottom-color: #111111; }
    .pp-tab:hover:not(.active) { color: #374151; }

    /* KPI row */
    .pp-kpi-row {
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px;
    }
    @media (max-width: 1023px) { .pp-kpi-row { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 479px)  { .pp-kpi-row { grid-template-columns: 1fr; } }
    .pp-kpi {
      background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
      padding: 16px 20px; position: relative; overflow: hidden;
    }
    .pp-kpi-accent {
      position: absolute; top: 0; left: 0; bottom: 0; width: 4px;
      border-radius: 10px 0 0 10px;
    }
    .pp-kpi-val {
      font-family: 'DM Sans', sans-serif;
      font-size: 2.2rem; font-weight: 700; line-height: 1; color: #0c1118; letter-spacing: .02em;
    }
    .pp-kpi-lbl {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .04em;
      color: #9ca3af; margin-top: 3px;
    }
    .pp-kpi-sub { font-size: 11.5px; color: #6b7280; margin-top: 2px; }

    /* Table card */
    .pp-table-card {
      background: #fff; border: 1px solid #e8ecf0; border-radius: 10px; overflow: hidden;
    }
    .pp-table-wrap { overflow-x: auto; }
    .pp-table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .pp-table thead tr {
      background: #f9fafb; border-bottom: 1px solid #e8ecf0;
    }
    .pp-table th {
      padding: 10px 16px; text-align: left;
      font-family: 'DM Sans', sans-serif;
      font-size: 9.5px; letter-spacing: .04em;
      color: #9ca3af; white-space: nowrap;
    }
    .pp-table td { padding: 13px 16px; vertical-align: top; }
    .pp-table tbody tr { border-bottom: 1px solid #f3f4f6; transition: background 120ms; }
    .pp-table tbody tr:last-child { border-bottom: none; }
    .pp-table tbody tr:hover { background: #fafafa; }

    /* Row accent stripe */
    .pp-row-pending    { border-left: 3px solid #111111; }
    .pp-row-processing { border-left: 3px solid #3b82f6; }
    .pp-row-completed  { border-left: 3px solid #10b981; }
    .pp-row-canceled   { border-left: 3px solid #ef4444; }
    .pp-row-default    { border-left: 3px solid #e2e8f0; }

    /* Status badges */
    .pp-badge {
      display: inline-flex; align-items: center;
      padding: 3px 9px; border-radius: 5px;
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .06em; font-weight: 500;
      border: 1px solid transparent;
    }
    .pp-badge-pending    { background: rgba(0,0,0,0.06); color: #333333; border-color: rgba(0,0,0,0.12); }
    .pp-badge-processing { background: rgba(59,130,246,.1);  color: #1e40af; border-color: rgba(59,130,246,.2); }
    .pp-badge-completed  { background: rgba(16,185,129,.1);  color: #065f46; border-color: rgba(16,185,129,.2); }
    .pp-badge-canceled   { background: rgba(239,68,68,.1);   color: #991b1b; border-color: rgba(239,68,68,.2); }
    .pp-badge-default    { background: #f3f4f6; color: #6b7280; }

    /* Inline source badges */
    .pp-source-org { background: rgba(99,102,241,.1); color: #3730a3; border-radius: 4px; padding: 2px 7px; font-size: 10px; }
    .pp-source-ind { background: #f3f4f6; color: #6b7280; border-radius: 4px; padding: 2px 7px; font-size: 10px; }

    /* Action btns in table */
    .pp-act-btn {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 10px; border-radius: 6px; font-size: 11.5px; font-weight: 500;
      cursor: pointer; border: none; transition: background 150ms;
    }
    .pp-act-view    { background: #0c1118; color: #fff; }
    .pp-act-view:hover { background: #1e2832; }
    .pp-act-delete  { background: rgba(239,68,68,.1); color: #b91c1c; }
    .pp-act-delete:hover { background: rgba(239,68,68,.2); }
    .pp-act-upload  { background: rgba(16,185,129,.12); color: #065f46; }
    .pp-act-upload:hover { background: rgba(16,185,129,.2); }
    .pp-act-editor  { background: #0c1118; color: #fff; }
    .pp-act-editor:hover { background: #1e2832; }

    /* Toggle switch */
    .pp-toggle {
      position: relative; width: 36px; height: 20px;
      border-radius: 10px; cursor: pointer;
      transition: background 200ms; flex-shrink: 0;
    }
    .pp-toggle-on  { background: #10b981; }
    .pp-toggle-off { background: #d1d5db; }
    .pp-toggle-knob {
      position: absolute; top: 2px; width: 16px; height: 16px;
      border-radius: 50%; background: #fff;
      box-shadow: 0 1px 3px rgba(0,0,0,.2);
      transition: left 200ms;
    }
    .pp-toggle-on  .pp-toggle-knob { left: 18px; }
    .pp-toggle-off .pp-toggle-knob { left: 2px; }

    /* Pagination */
    .pp-pagination {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 16px; border-top: 1px solid #f3f4f6;
      background: #f9fafb; flex-wrap: wrap; gap: 8px;
    }
    .pp-pagination-info {
      font-family: 'DM Sans', sans-serif;
      font-size: 11px; letter-spacing: .04em; color: #9ca3af;
      display: flex; align-items: center; gap: 8px;
    }
    .pp-pagination-ctrl { display: flex; align-items: center; gap: 6px; }
    .pp-page-btn {
      padding: 5px 12px; border-radius: 6px; font-size: 12px;
      border: 1px solid #e2e8f0; background: #fff; color: #374151;
      cursor: pointer; transition: background 150ms;
    }
    .pp-page-btn:hover:not(:disabled) { background: #f3f4f6; }
    .pp-page-btn:disabled { color: #d1d5db; cursor: default; border-color: #f3f4f6; }

    /* Empty state */
    .pp-empty {
      padding: 56px 20px; text-align: center;
    }
    .pp-empty-icon { color: #d1d5db; margin: 0 auto 12px; display: block; }
    .pp-empty-title { font-size: 14px; font-weight: 600; color: #374151; }
    .pp-empty-sub { font-size: 12px; color: #9ca3af; margin-top: 4px; }

    /* Company access table */
    .pp-org-card {
      background: #fff; border: 1px solid #e8ecf0; border-radius: 10px; overflow: hidden;
    }
    .pp-org-head {
      padding: 16px 20px; border-bottom: 1px solid #f3f4f6;
      display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 10px;
    }
    .pp-org-title { font-size: 14px; font-weight: 600; color: #0c1118; }
    .pp-org-sub { font-size: 12px; color: #9ca3af; margin-top: 2px; }
    .pp-search-input {
      padding: 7px 12px; border: 1px solid #e2e8f0; border-radius: 7px;
      font-size: 13px; color: #0c1118; outline: none; width: 200px;
      transition: border-color 150ms;
    }
    .pp-search-input:focus { border-color: #111111; }

    /* Loading spinner */
    .pp-spinner {
      width: 40px; height: 40px; border: 3px solid #e8ecf0;
      border-top-color: #0c1118; border-radius: 50%;
      animation: ppSpin 0.7s linear infinite;
      margin: 0 auto;
    }
    @keyframes ppSpin { to { transform: rotate(360deg); } }

    /* PDF Viewer modal */
    .pp-viewer-backdrop {
      position: fixed; inset: 0; z-index: 50;
      background: rgba(0,0,0,.65); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center; padding: 24px;
    }
    .pp-viewer-modal {
      background: #0c1118; border-radius: 12px; overflow: hidden;
      width: 100%; max-width: 900px; height: 85vh;
      display: flex; flex-direction: column;
      box-shadow: 0 24px 64px rgba(0,0,0,.5);
    }
    .pp-viewer-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px 20px; border-bottom: 1px solid rgba(255,255,255,.1);
    }
    .pp-viewer-title { font-size: 14px; font-weight: 600; color: #fff; }
    .pp-viewer-sub { font-family: 'DM Sans', sans-serif; font-size: 10px; color: rgba(255,255,255,.4); margin-top: 2px; letter-spacing: .06em; }
    .pp-viewer-close {
      width: 32px; height: 32px; border-radius: 50%;
      background: rgba(255,255,255,.07); border: none; color: rgba(255,255,255,.6);
      cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: background 150ms;
    }
    .pp-viewer-close:hover { background: rgba(255,255,255,.15); color: #fff; }

    /* Filter drawer */
    .pp-drawer-backdrop {
      position: fixed; inset: 0; z-index: 40; pointer-events: none;
    }
    .pp-drawer-backdrop.open { pointer-events: auto; }
    .pp-drawer-bg {
      position: absolute; inset: 0; background: rgba(0,0,0,.35);
      backdrop-filter: blur(2px);
      opacity: 0; transition: opacity 260ms;
    }
    .pp-drawer-backdrop.open .pp-drawer-bg { opacity: 1; }
    .pp-drawer {
      position: absolute; right: 0; top: 0; bottom: 0;
      width: 360px; max-width: 100vw;
      background: #fff; border-left: 1px solid #e8ecf0;
      box-shadow: -8px 0 32px rgba(0,0,0,.1);
      transform: translateX(100%); transition: transform 280ms cubic-bezier(.4,0,.2,1);
      display: flex; flex-direction: column;
    }
    .pp-drawer-backdrop.open .pp-drawer { transform: translateX(0); }
    .pp-drawer-head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 20px; border-bottom: 1px solid #f3f4f6;
    }
    .pp-drawer-title { font-size: 14px; font-weight: 600; color: #0c1118; }
    .pp-drawer-sub { font-size: 12px; color: #9ca3af; }
    .pp-drawer-body { flex: 1; overflow-y: auto; padding: 20px; }
    .pp-drawer-foot {
      padding: 16px 20px; border-top: 1px solid #f3f4f6;
      display: flex; justify-content: space-between; gap: 10px;
    }
    .pp-field-label {
      display: block; font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .04em;
      color: #9ca3af; margin-bottom: 6px;
    }
    .pp-field-input, .pp-field-select {
      width: 100%; padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 7px;
      font-size: 13px; color: #0c1118; background: #fff; outline: none;
      transition: border-color 150ms;
    }
    .pp-field-input:focus, .pp-field-select:focus { border-color: #111111; }

    @media (max-width: 767px) {
      .pp-kpi-row { grid-template-columns: repeat(2,1fr) !important; }
      .pp-kpi { padding: 12px 14px; }
      .pp-kpi-val { font-size: 1.6rem; }
      .pp-kpi-sub { display: none; }
      .pp-search-input { width: 100%; }
      .pp-tabs { overflow-x: auto; }
      .pp-tab { white-space: nowrap; font-size: 12px; padding: 10px 12px; }
    }
  `;
  document.head.appendChild(el);
}

/* ── Helpers ──────────────────────────────────────────────────── */
function formatStatusLabel(status) {
  if (!status) return "-";
  return status.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function statusBadgeClass(status) {
  switch (status) {
    case "PENDING_PAYMENT": case "PENDING": return "pp-badge pp-badge-pending";
    case "PROCESSING":  return "pp-badge pp-badge-processing";
    case "COMPLETED":   return "pp-badge pp-badge-completed";
    case "CANCELED": case "CANCELLED": return "pp-badge pp-badge-canceled";
    default: return "pp-badge pp-badge-default";
  }
}

function rowClass(status) {
  switch (status) {
    case "PENDING_PAYMENT": case "PENDING": return "pp-row-pending";
    case "PROCESSING":  return "pp-row-processing";
    case "COMPLETED":   return "pp-row-completed";
    case "CANCELED": case "CANCELLED": return "pp-row-canceled";
    default: return "pp-row-default";
  }
}

const ACCENT = { pending: "#111111", processing: "#3b82f6", completed: "#10b981", total: "#0c1118" };

function PlanMobileCard({ r, onView, onDelete, onUpload, onOpenEditor, onToggleAccess, uploadingId }) {
  const isUnlocked = r.requesterAccessLock === "unlocked";
  const isIndividual = r.source === "individual";
  return (
    <div style={{ background: "#fff", border: "1px solid #e8ecf0", borderRadius: 10, padding: "14px 16px", marginBottom: 10, borderLeft: `3px solid ${r.status === "COMPLETED" ? "#10b981" : r.status === "PROCESSING" ? "#3b82f6" : r.status?.startsWith("PENDING") ? "#111111" : "#e2e8f0"}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#0c1118" }}>{r.projectTitle}</div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{r.type === "REWORK" ? `Rework #${r.reworkRound || 1}` : "New request"}</div>
        </div>
        <span className={statusBadgeClass(r.status)}>{formatStatusLabel(r.status)}</span>
      </div>
      <div style={{ fontSize: 12, color: "#374151", marginBottom: 4 }}>{r.requesterName} · <span style={{ color: "#9ca3af" }}>{r.requesterEmail}</span></div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        {r.source === "org" ? <span className="pp-source-org">Company</span> : <span className="pp-source-ind">Individual</span>}
        {r.orgName && <span style={{ fontSize: 11, color: "#6b7280" }}>{r.orgName}</span>}
        <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {r.fileUrl ? (
          <>
            <button className="pp-act-btn pp-act-view" onClick={() => onView(r)}><FaEye size={12} /> View PDF</button>
            <button className="pp-act-btn pp-act-delete" onClick={() => onDelete(r.id)}>Delete PDF</button>
          </>
        ) : ["PROCESSING", "COMPLETED"].includes(r.status) ? (
          <label className="pp-act-btn pp-act-upload" style={{ cursor: uploadingId === r.id ? "not-allowed" : "pointer" }}>
            {uploadingId === r.id ? "Uploading…" : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Upload PDF</>}
            <input type="file" accept="application/pdf" style={{ display: "none" }} disabled={uploadingId === r.id} onChange={(e) => onUpload(r.id, e.target.files?.[0], e.target)} />
          </label>
        ) : null}
        {r.editorUrl && (
          <button className="pp-act-btn pp-act-editor" onClick={() => window.open(r.editorUrl, "_blank", "noopener,noreferrer")}>Open Editor</button>
        )}
        {isIndividual && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>{isUnlocked ? "Unlocked" : "Locked"}</span>
            <div className={`pp-toggle ${isUnlocked ? "pp-toggle-on" : "pp-toggle-off"}`} onClick={() => onToggleAccess(r.id, isUnlocked)}>
              <div className="pp-toggle-knob" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrgMobileCard({ o, onToggle, updating }) {
  const isLocked = o.accessLock === "locked";
  return (
    <div style={{ background: "#fff", border: "1px solid #e8ecf0", borderRadius: 10, padding: "14px 16px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#0c1118", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.orgName}</div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{o.stats?.total ?? 0} requests · {o.stats?.completed ?? 0} completed</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <span className={`pp-badge ${isLocked ? "pp-badge-canceled" : "pp-badge-completed"}`}>{isLocked ? "Locked" : "Unlocked"}</span>
        <button className="pp-btn pp-btn-outline" style={{ padding: "5px 12px", fontSize: 12 }} onClick={() => onToggle(o)} disabled={updating}>
          {updating ? "…" : isLocked ? "Unlock" : "Lock"}
        </button>
      </div>
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────── */
export default function PurchasePlansPage() {
  useEffect(() => { injectStyles(); }, []);
  const isMobile = useIsMobile();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);

  const [orgRows, setOrgRows] = useState([]);
  const [loadingOrgAccess, setLoadingOrgAccess] = useState(true);
  const [updatingOrgId, setUpdatingOrgId] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [titleFilter, setTitleFilter] = useState("");
  const [requesterFilter, setRequesterFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState("requests");
  const [orgSearch, setOrgSearch] = useState("");
  const [viewer, setViewer] = useState(null);

  const backendBase = process.env.REACT_APP_BACKEND_URL;

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (sourceFilter && sourceFilter !== "all") params.set("source", sourceFilter);
      if (titleFilter.trim()) params.set("q", titleFilter.trim());
      if (requesterFilter.trim()) params.set("requester", requesterFilter.trim());
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo);
      const query = params.toString();
      const url = `${backendBase}/api/purchase-plans${query ? `?${query}` : ""}`;
      const r = await api.get(url);
      setRows(Array.isArray(r) ? r : []);
      setPage(1);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load engineering plan requests");
    } finally {
      setLoading(false);
    }
  }

  async function loadOrgAccess() {
    setLoadingOrgAccess(true);
    try {
      const r = await api.get(`${backendBase}/api/purchase-plans/org-access`);
      setOrgRows(Array.isArray(r) ? r : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load company access list");
    } finally {
      setLoadingOrgAccess(false);
    }
  }

  useEffect(() => {
    load();
    loadOrgAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    if (page > pageCount) setPage(pageCount);
  }, [rows.length, pageSize, page]);

  async function toggleOrgAccess(org) {
    const nextLock = org.accessLock === "locked" ? "unlocked" : "locked";
    setUpdatingOrgId(org.orgId);
    try {
      const res = await fetch(`${backendBase}/api/purchase-plans/org-access/${org.orgId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessLock: nextLock }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || "Failed"); }
      toast.success(nextLock === "locked" ? "Company access locked" : "Company access unlocked");
      loadOrgAccess();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to update company access");
    } finally {
      setUpdatingOrgId(null);
    }
  }

  async function complete(id, file, inputElement) {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please upload a PDF file"); if (inputElement) inputElement.value = ""; return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File size must be less than 10MB"); if (inputElement) inputElement.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingId(id);
    try {
      const response = await fetch(`${backendBase}/api/purchase-plans/${id}/complete`, { method: "POST", body: fd, credentials: "include" });
      if (!response.ok) { const d = await response.json().catch(() => ({})); throw new Error(d.message || `Upload failed with status ${response.status}`); }
      toast.success("Plan PDF uploaded successfully");
      if (inputElement) inputElement.value = "";
      load();
    } catch (e) {
      console.error("Upload error:", e);
      toast.error(e?.message || "Upload failed");
      if (inputElement) inputElement.value = "";
    } finally {
      setUploadingId(null);
    }
  }

  async function removeFile(id) {
    try {
      const res = await fetch(`${backendBase}/api/purchase-plans/${id}/file`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete file");
      toast.success("Plan PDF deleted");
      load();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Delete failed");
    }
  }

  async function updateAccess(id, payload) {
    try {
      const res = await fetch(`${backendBase}/api/purchase-plans/${id}/access`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || "Failed to update access"); }
      toast.success("Access updated");
      load();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to update access");
    }
  }

  const kpis = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => (r.status || "").startsWith("PENDING")).length;
    const processing = rows.filter((r) => r.status === "PROCESSING").length;
    const completed = rows.filter((r) => r.status === "COMPLETED").length;
    const bySource = rows.reduce((acc, r) => { if (r.source === "org") acc.org += 1; else acc.individual += 1; return acc; }, { org: 0, individual: 0 });
    return { total, pending, processing, completed, bySource };
  }, [rows]);

  const statusChartData = useMemo(() => {
    const labels = ["Pending", "Processing", "Completed", "Canceled"];
    const statusKeys = ["PENDING_PAYMENT", "PROCESSING", "COMPLETED", "CANCELED"];
    const counts = statusKeys.map((key) => rows.filter((r) => {
      if (key === "PENDING_PAYMENT") return (r.status || "").startsWith("PENDING");
      if (key === "CANCELED") return r.status === "CANCELED" || r.status === "CANCELLED";
      return r.status === key;
    }).length);
    const max = Math.max(...counts, 1);
    return labels.map((label, idx) => ({ label, value: counts[idx], heightPct: (counts[idx] / max) * 100 }));
  }, [rows]);

  const companyStatusChartData = useMemo(() => {
    const orgOnly = rows.filter((r) => r.source === "org");
    const labels = ["Pending", "Processing", "Completed", "Canceled"];
    const statusKeys = ["PENDING_PAYMENT", "PROCESSING", "COMPLETED", "CANCELED"];
    const counts = statusKeys.map((key) => orgOnly.filter((r) => {
      if (key === "PENDING_PAYMENT") return (r.status || "").startsWith("PENDING");
      if (key === "CANCELED") return r.status === "CANCELED" || r.status === "CANCELLED";
      return r.status === key;
    }).length);
    const max = Math.max(...counts, 1);
    return labels.map((label, idx) => ({ label, value: counts[idx], heightPct: (counts[idx] / max) * 100 }));
  }, [rows]);

  const filteredOrgRows = useMemo(() => {
    const term = orgSearch.trim().toLowerCase();
    if (!term) return orgRows;
    return orgRows.filter((o) => (o.orgName || "").toLowerCase().includes(term));
  }, [orgRows, orgSearch]);

  const topOrgByRequests = useMemo(() => {
    return [...orgRows].filter((o) => (o.stats?.total || 0) > 0).sort((a, b) => (b.stats?.total || 0) - (a.stats?.total || 0)).slice(0, 5);
  }, [orgRows]);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(rows.length, startIndex + pageSize);
  const paginatedRows = useMemo(() => rows.slice(startIndex, endIndex), [rows, startIndex, endIndex]);

  /* ── Loading state ── */
  if (loading && rows.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "16px" }}>
        <div className="pp-spinner" />
        <p style={{ fontFamily: "'DM Sans',sans-serif", color: "#9ca3af", fontSize: "13px" }}>
          Loading engineering plan requests…
        </p>
      </div>
    );
  }

  return (
    <div className="pp-wrap">
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <div className="pp-page-title" class={isMobile?"font-14px":"font-16px"}>Engineering Plan Requests</div>
          <div className="pp-page-sub">Purchase plan management · CEED Console</div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="pp-btn pp-btn-outline"
            onClick={() => { load(); loadOrgAccess(); }}
            disabled={loading}
            title="Refresh"
          >
            <svg width={isMobile ? "12" : "14"} height={isMobile ? "12" : "14"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {!isMobile && "Refresh"}
          </button>
          <button className="pp-btn pp-btn-dark" onClick={() => setFiltersOpen(true)}>
            <svg width={isMobile ? "12" : "14"} height={isMobile ? "12" : "14"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {!isMobile && "Filters"}
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="pp-tabs">
        <button className={`pp-tab ${activeTab === "requests" ? "active" : ""}`} onClick={() => setActiveTab("requests")}>
          Requests
          {rows.length > 0 && (
            <span style={{ marginLeft: "8px", background: "#f3f4f6", color: "#6b7280", padding: "1px 7px", borderRadius: "10px", fontSize: "11px" }}>
              {rows.length}
            </span>
          )}
        </button>
        <button className={`pp-tab ${activeTab === "companies" ? "active" : ""}`} onClick={() => setActiveTab("companies")}>
          Company Plan Access
          {orgRows.length > 0 && (
            <span style={{ marginLeft: "8px", background: "#f3f4f6", color: "#6b7280", padding: "1px 7px", borderRadius: "10px", fontSize: "11px" }}>
              {orgRows.length}
            </span>
          )}
        </button>
      </div>

      {/* ══════════ TAB: REQUESTS ══════════ */}
      {activeTab === "requests" && (
        <>
          {/* KPI row */}
          <div className="pp-kpi-row" style={isMobile ? { gridTemplateColumns: "repeat(2,1fr)" } : undefined}>
            {[
              { accent: ACCENT.total, val: kpis.total, lbl: "Total Requests", sub: `${kpis.bySource.org} company · ${kpis.bySource.individual} individual` },
              { accent: ACCENT.pending, val: kpis.pending, lbl: "Pending", sub: "Awaiting action" },
              { accent: ACCENT.processing, val: kpis.processing, lbl: "Processing", sub: "Under review" },
              { accent: ACCENT.completed, val: kpis.completed, lbl: "Completed", sub: "Plan uploaded" },
            ].map((k) => (
              <div className="pp-kpi" key={k.lbl}>
                <div className="pp-kpi-accent" style={{ background: k.accent }} />
                <div className="pp-kpi-val">{k.val}</div>
                <div className="pp-kpi-lbl">{k.lbl}</div>
                <div className="pp-kpi-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Table / Mobile cards */}
          {isMobile ? (
            <div>
              {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 13 }}>Loading…</div>
              ) : paginatedRows.length === 0 ? (
                <div className="pp-empty"><div className="pp-empty-title">No requests found</div><div className="pp-empty-sub">Try adjusting your filters</div></div>
              ) : (
                paginatedRows.map((r) => (
                  <PlanMobileCard
                    key={r.id} r={r}
                    onView={(r) => setViewer({ url: r.fileUrl, title: r.projectTitle || "Plan PDF" })}
                    onDelete={removeFile}
                    onUpload={complete}
                    onOpenEditor={(url) => window.open(url, "_blank", "noopener,noreferrer")}
                    onToggleAccess={(id, isUnlocked) => updateAccess(id, { requesterAccessLock: isUnlocked ? "locked" : "unlocked" })}
                    uploadingId={uploadingId}
                  />
                ))
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                <button className="pp-page-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
                <span style={{ fontSize: 12, color: "#6b7280" }}>Page {page} of {pageCount}</span>
                <button className="pp-page-btn" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next →</button>
              </div>
            </div>
          ) : (
            <div className="pp-table-card">
              <div className="pp-table-wrap">
                <table className="pp-table">
                  <thead>
                    <tr>
                      <th>Design</th>
                      <th>Requester</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Plan PDF</th>
                      <th>Access / Approval</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8}>
                          <div style={{ padding: "48px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", color: "#9ca3af", fontSize: "13px" }}>
                            <div className="pp-spinner" style={{ width: "20px", height: "20px", borderWidth: "2px" }} />
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={8}>
                          <div className="pp-empty">
                            <svg className="pp-empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
                              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div className="pp-empty-title">No engineering plan requests found</div>
                            <div className="pp-empty-sub">Try adjusting your filters</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((r) => {
                        const isIndividual = r.source === "individual";
                        const isOrg = r.source === "org";
                        const accessLabel = r.requesterAccessLock === "locked" ? "Locked" : r.requesterAccessLock === "unlocked" ? "Unlocked" : "-";
                        const typeLabel = r.type === "REWORK" ? `Rework #${(r.reworkRound ?? 0) || 1}` : "New request";
                        const isUnlocked = r.requesterAccessLock === "unlocked";

                        return (
                          <tr key={r.id} className={rowClass(r.status)}>
                            <td>
                              <div style={{ fontWeight: 600, color: "#0c1118", fontSize: "13px" }}>{r.projectTitle}</div>
                              <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{typeLabel}</div>
                            </td>
                            <td>
                              <div style={{ fontSize: "13px", color: "#374151" }}>{r.requesterName}</div>
                              <div style={{ fontSize: "11px", color: "#9ca3af" }}>{r.requesterEmail}</div>
                            </td>
                            <td>
                              {isOrg ? (
                                <div>
                                  <span className="pp-source-org">Company</span>
                                  {r.orgName && <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}>{r.orgName}</div>}
                                </div>
                              ) : (
                                <span className="pp-source-ind">Individual</span>
                              )}
                            </td>
                            <td>
                              <span className={statusBadgeClass(r.status)}>{formatStatusLabel(r.status)}</span>
                            </td>
                            <td>
                              <div style={{ fontSize: "12px", color: "#6b7280", fontFamily: "'DM Sans', sans-serif" }}>
                                {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "-"}
                              </div>
                              <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                                {r.createdAt ? new Date(r.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                              </div>
                            </td>
                            <td>
                              {r.fileUrl ? (
                                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                  <button className="pp-act-btn pp-act-view" onClick={() => setViewer({ url: r.fileUrl, title: r.projectTitle || "Plan PDF" })}>
                                    <FaEye size={12} /> View
                                  </button>
                                  <button className="pp-act-btn pp-act-delete" onClick={() => removeFile(r.id)}>Delete</button>
                                </div>
                              ) : ["PROCESSING", "COMPLETED"].includes(r.status) && !r.fileUrl ? (
                                <label className={`pp-act-btn pp-act-upload${uploadingId === r.id ? " pp-btn-disabled" : ""}`} style={{ cursor: uploadingId === r.id ? "not-allowed" : "pointer" }}>
                                  {uploadingId === r.id ? (
                                    <><div style={{ width: "12px", height: "12px", border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", animation: "ppSpin .7s linear infinite" }} />Uploading…</>
                                  ) : (
                                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>Upload PDF</>
                                  )}
                                  <input type="file" accept="application/pdf" style={{ display: "none" }} disabled={uploadingId === r.id} onChange={(e) => complete(r.id, e.target.files?.[0], e.target)} />
                                </label>
                              ) : (
                                <span style={{ color: "#d1d5db", fontSize: "12px" }}>—</span>
                              )}
                            </td>
                            <td>
                              {isIndividual ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>{accessLabel === "-" ? "Not configured" : accessLabel}</span>
                                    <div className={`pp-toggle ${isUnlocked ? "pp-toggle-on" : "pp-toggle-off"}`} onClick={() => updateAccess(r.id, { requesterAccessLock: isUnlocked ? "locked" : "unlocked" })}>
                                      <div className="pp-toggle-knob" />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "4px" }}>Company approval</div>
                                  <span className={`pp-badge ${(r.companyApprovalStatus || "pending") === "approved" ? "pp-badge-completed" : (r.companyApprovalStatus || "pending") === "rejected" ? "pp-badge-canceled" : "pp-badge-pending"}`}>
                                    {((r.companyApprovalStatus || "pending").charAt(0).toUpperCase() + (r.companyApprovalStatus || "pending").slice(1))}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {r.editorUrl && (
                                <button className="pp-act-btn pp-act-editor" onClick={() => window.open(r.editorUrl, "_blank", "noopener,noreferrer")}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                  Open Editor
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="pp-pagination">
                <div className="pp-pagination-info">
                  <span>Rows per page:</span>
                  <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ border: "1px solid #e2e8f0", borderRadius: "5px", padding: "3px 8px", fontSize: "12px", background: "#fff", color: "#374151" }}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span style={{ marginLeft: "4px" }}>{rows.length === 0 ? "0" : `${startIndex + 1}–${endIndex}`} of {rows.length}</span>
                </div>
                <div className="pp-pagination-ctrl">
                  <button className="pp-page-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
                  <span style={{ fontSize: "12px", color: "#6b7280", padding: "0 4px" }}>Page {page} of {pageCount}</span>
                  <button className="pp-page-btn" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next →</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════ TAB: COMPANY ACCESS ══════════ */}
      {activeTab === "companies" && (
        <div className="pp-org-card">
          <div className="pp-org-head" style={{ flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "flex-end" }}>
            <div>
              <div className="pp-org-title">Company Plan Access</div>
              <div className="pp-org-sub">Lock or unlock engineered plan access per company.</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", width: isMobile ? "100%" : "auto" }}>
              <input
                type="text"
                className="pp-search-input"
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                placeholder="Search company…"
                style={{ width: isMobile ? "100%" : undefined }}
              />
              {loadingOrgAccess && (
                <div className="pp-spinner" style={{ width: "16px", height: "16px", borderWidth: "2px" }} />
              )}
            </div>
          </div>

          {filteredOrgRows.length === 0 ? (
            <div className="pp-empty">
              <div className="pp-empty-title">No companies found</div>
              <div className="pp-empty-sub">Try a different search term</div>
            </div>
          ) : isMobile ? (
            <div style={{ padding: "12px" }}>
              {filteredOrgRows.map((o) => (
                <OrgMobileCard key={o.orgId} o={o} onToggle={toggleOrgAccess} updating={updatingOrgId === o.orgId} />
              ))}
            </div>
          ) : (
            <div className="pp-table-wrap">
              <table className="pp-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Created</th>
                    <th>Access</th>
                    <th>Requests</th>
                    <th style={{ textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrgRows.map((o) => (
                    <tr key={o.orgId}>
                      <td><div style={{ fontWeight: 600, fontSize: "13px", color: "#0c1118" }}>{o.orgName}</div></td>
                      <td><div style={{ fontSize: "12px", color: "#6b7280", fontFamily: "'DM Sans', sans-serif" }}>{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "-"}</div></td>
                      <td><span className={`pp-badge ${o.accessLock === "locked" ? "pp-badge-canceled" : "pp-badge-completed"}`}>{o.accessLock === "locked" ? "Locked" : "Unlocked"}</span></td>
                      <td><div style={{ fontSize: "12px", color: "#6b7280" }}>{o.stats?.total ?? 0} total · {o.stats?.completed ?? 0} completed</div></td>
                      <td style={{ textAlign: "right" }}>
                        <button className="pp-btn pp-btn-outline" style={{ padding: "5px 12px", fontSize: "12px" }} onClick={() => toggleOrgAccess(o)} disabled={updatingOrgId === o.orgId}>
                          {updatingOrgId === o.orgId ? "Updating…" : o.accessLock === "locked" ? "Unlock" : "Lock"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── PDF Viewer modal ── */}
      {viewer && (
        <div className="pp-viewer-backdrop" onClick={() => setViewer(null)} style={isMobile ? { padding: 0 } : undefined}>
          <div className="pp-viewer-modal" onClick={(e) => e.stopPropagation()} style={isMobile ? { borderRadius: 0, height: "100vh", maxWidth: "100%" } : undefined}>
            <div className="pp-viewer-header">
              <div>
                <div className="pp-viewer-title">{viewer.title}</div>
                <div className="pp-viewer-sub">ENGINEERING PLAN · PDF VIEWER</div>
              </div>
              <button className="pp-viewer-close" onClick={() => setViewer(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div style={{ flex: 1, background: "#1a1f2e" }}>
              <iframe src={viewer.url} title="Engineering plan PDF" style={{ width: "100%", height: "100%", border: "none" }} />
            </div>
          </div>
        </div>
      )}

      {/* ── Filter drawer ── */}
      <div className={`pp-drawer-backdrop ${filtersOpen ? "open" : ""}`}>
        <div className="pp-drawer-bg" onClick={() => setFiltersOpen(false)} />
        <div className="pp-drawer">
          <div className="pp-drawer-head">
            <div>
              <div className="pp-drawer-title">Filters</div>
              <div className="pp-drawer-sub">Narrow down plan requests</div>
            </div>
            <button
              style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}
              onClick={() => setFiltersOpen(false)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="pp-drawer-body">
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label className="pp-field-label">Design Title</label>
                <input type="text" className="pp-field-input" value={titleFilter} onChange={(e) => setTitleFilter(e.target.value)} placeholder="Search by project name…" />
              </div>
              <div>
                <label className="pp-field-label">Requester</label>
                <input type="text" className="pp-field-input" value={requesterFilter} onChange={(e) => setRequesterFilter(e.target.value)} placeholder="Name or email…" />
              </div>
              <div>
                <label className="pp-field-label">Status</label>
                <select className="pp-field-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="PENDING_PAYMENT">Pending payment</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELED">Canceled</option>
                </select>
              </div>
              <div>
                <label className="pp-field-label">Source</label>
                <select className="pp-field-select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="org">Company requests</option>
                  <option value="individual">Individual users</option>
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label className="pp-field-label">From (created)</label>
                  <input type="date" className="pp-field-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <label className="pp-field-label">To (created)</label>
                  <input type="date" className="pp-field-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="pp-drawer-foot">
            <button
              className="pp-btn pp-btn-outline"
              onClick={() => {
                setStatusFilter("all"); setSourceFilter("all");
                setTitleFilter(""); setRequesterFilter("");
                setDateFrom(""); setDateTo(""); load();
              }}
            >
              Reset
            </button>
            <button
              className="pp-btn pp-btn-dark"
              onClick={() => { load(); setFiltersOpen(false); }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
